import { Backdrop, Box, Card, CardContent, CircularProgress, Typography } from '@mui/material';

import { useAppSelector } from '@/app/hooks';

/**
 * Global loading overlay component
 *
 * Displays a full-screen backdrop with a centered loading spinner and message
 * when global loading state is active.
 *
 * Features:
 * - Full-screen semi-transparent backdrop (blocks all interaction)
 * - Centered card with spinner and custom message
 * - Smooth fade animations (300ms)
 * - High z-index (9999) to appear above all content
 * - Matches theme colors
 *
 * Usage:
 * This component should be placed in AppShell. It automatically shows/hides
 * based on Redux loading state controlled via useGlobalLoading hook.
 *
 * @example
 * ```tsx
 * // In AppShell.tsx
 * <Box>
 *   <GlobalLoadingOverlay />
 *   <Header />
 *   <Sidebar />
 *   <MainContent>{children}</MainContent>
 * </Box>
 * ```
 */
export const GlobalLoadingOverlay = () => {
  const { isLoading, message } = useAppSelector((state) => state.loading);

  return (
    <Backdrop
      open={isLoading}
      sx={{
        zIndex: 9999,
        bgcolor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(2px)',
      }}
      // Prevent clicks from closing the backdrop
      onClick={(e) => e.stopPropagation()}
    >
      <Card
        elevation={8}
        sx={{
          minWidth: 300,
          maxWidth: 500,
          textAlign: 'center',
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              py: 2,
            }}
          >
            <CircularProgress size={48} thickness={4} />
            <Typography variant="h6" color="text.primary">
              {message}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Backdrop>
  );
};
